// services/bookingService.js
import { get, post, put } from '@aws-amplify/api-rest';
import { fetchAuthSession } from 'aws-amplify/auth';
import { withRetry, createResilientApiClient } from '../utils/apiRetry';

const API_NAME = 'CourseBookingApi';
console.log("✅ API_NAME IS NOW:", API_NAME);

// Resend invoice email for a booking (admin only)
export async function resendInvoiceAdmin(bookingId) {
  const headers = await getAuthHeaders();
  return handleApiCall(() =>
    post({
      apiName: API_NAME,
      path: `/admin/bookings/${bookingId}/resend-invoice`,
      options: { headers }
    })
  );
}

// Create resilient API client for handling network errors
const resilientClient = createResilientApiClient({
  circuitBreaker: {
    failureThreshold: 10,
    resetTimeout: 30000, // 30 seconds
  },
  batchQueue: {
    batchSize: 3,
    batchDelay: 200
  }
});

// Helper to get auth headers
async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    if (!session || !session.tokens || !session.tokens.idToken) {
      console.error('Authentication Failure: No valid session or token found.');
      throw new Error('No authentication token');
    }
    const headers = {
      Authorization: `Bearer ${session.tokens.idToken.toString()}`
    };
    //console.log('Authorization Headers:', headers);
    return headers;
  } catch (error) {
    console.error('Error fetching auth session:', error);
    throw error;
  }
}

// Enhanced error handling wrapper (same as courseService.js)
const handleApiCall = async (apiCall) => {
  try {
    let response = await apiCall();

    // Unwrap { response: Promise }
    if (response && response.response && typeof response.response.then === 'function') {
      response = await response.response;
    }
    
    // Parse fetch Response
    if (response && typeof response.json === 'function') {
      response = await response.json();
    }
    
    // Handle ReadableStream
    if (response && response.body && typeof response.body.getReader === 'function') {
      const reader = response.body.getReader();
      let chunks = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        // Use concat instead of spread to avoid stack overflow with large arrays
        if (value) {
          chunks = chunks.concat(Array.from(value));
        }
      }
      const text = new TextDecoder().decode(new Uint8Array(chunks));
      try {
        response = { ...response, ...JSON.parse(text) };
      } catch (e) {
        // Not JSON, ignore
      }
    }

    // If .body is a string, parse it and merge
    if (response && typeof response.body === 'string') {
      try {
        response = { ...response, ...JSON.parse(response.body) };
      } catch (e) {
        // Not JSON, ignore
      }
    }

    return response;
  } catch (error) {
    console.error(`API Error in ${apiCall.name}:`, error);
    
    // Check if it's an auth error
    if (error.statusCode === 401 || error.name === 'NotAuthorizedException') {
      console.error('Authentication error - user may need to sign in again');
    }
    
    throw error;
  }
};

// ============================================
// STUDENT BOOKING FUNCTIONS
// ============================================

/**
 * Create a new booking (reserve slot)
 */
export const createBooking = (bookingData) => handleApiCall(async function createBooking() {
  console.log('Creating booking with data:', bookingData);
  return await post({
    apiName: API_NAME,
    path: 'bookings',
    options: {
      body: bookingData,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Create a new booking with automatic retry on network errors
 * @param {Object} bookingData - The booking data
 * @param {Object} options - Options for retry behavior
 * @returns {Promise} The booking result
 */
export const createBookingWithRetry = async (bookingData, options = {}) => {
  const { onRetry, maxAttempts = 3 } = options;
  
  console.log('📝 Creating booking with retry logic...', bookingData);
  
  const apiCall = async () => {
    const headers = await getAuthHeaders();
    let response = await post({
      apiName: API_NAME,
      path: 'bookings',
      options: {
        body: bookingData,
        headers
      }
    });
    
    // Handle Amplify response wrapper
    if (response && response.response && typeof response.response.then === 'function') {
      response = await response.response;
    }
    if (response && typeof response.json === 'function') {
      response = await response.json();
    }
    
    return response;
  };
  
  try {
    const result = await withRetry(
      apiCall,
      {
        maxAttempts,
        initialDelay: 1000,
        maxDelay: 5000,
        retryableStatusCodes: [408, 429, 500, 502, 503, 504],
      },
      onRetry
    );
    
    console.log('✅ Booking created successfully:', result);
    return result;
    
  } catch (error) {
    console.error('❌ Failed to create booking after retries:', error);
    
    // Check if it's a "course full" error (don't retry these)
    if (error.lastError?.response?.status === 409 || 
        error.lastError?.message?.includes('Course full')) {
      throw new Error('Course is full - no spots available');
    }
    
    throw error;
  }
};

/**
 * Get booking by ID
 */
export const getBookingById = (bookingId) => handleApiCall(async function getBookingById() {
  return await get({
    apiName: API_NAME,
    path: `bookings/${bookingId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Update booking (form data, status, etc.)
 */
export const updateBooking = (bookingId, updateData) => handleApiCall(async function updateBooking() {
  return await put({
    apiName: API_NAME,
    path: `bookings/${bookingId}`,
    options: {
      body: updateData,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Release booking slot (for expired reservations)
 */
export const releaseBookingSlot = (bookingId) => handleApiCall(async function releaseBookingSlot() {
  return await post({
    apiName: API_NAME,
    path: `bookings/${bookingId}/release`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Cancel booking
 */
export const cancelBooking = (bookingId) => handleApiCall(async function cancelBooking() {
  return await post({
    apiName: API_NAME,
    path: `bookings/${bookingId}/cancel`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Complete booking
 */
export const completeBooking = (bookingId) => handleApiCall(async function completeBooking() {
  return await post({
    apiName: API_NAME,
    path: `bookings/${bookingId}/complete`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get current student's bookings
 */
export const getMyBookings = () => handleApiCall(async function getMyBookings() {
  return await get({
    apiName: API_NAME,
    path: 'students/me/bookings',
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get specific booking for current student
 */
export const getMyBookingById = (bookingId) => handleApiCall(async function getMyBookingById() {
  return await get({
    apiName: API_NAME,
    path: `students/me/bookings/${bookingId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

// ============================================
// ADMIN BOOKING FUNCTIONS
// ============================================

/**
 * Get all bookings with admin data (includes payment calculations, available slots, etc.)
 * ADMIN ONLY - requires admin group
 */
// Same child booked more than once on a course (matched on name + date of birth, not email)
export const getDuplicateStudentsAdmin = (courseId, { includeCancelled = false } = {}) => handleApiCall(async function getDuplicateStudentsAdmin() {
  const headers = await getAuthHeaders();
  const qs = includeCancelled ? '?includeCancelled=1' : '';
  return get({
    apiName: API_NAME,
    path: `admin/courses/${encodeURIComponent(courseId)}/duplicate-students${qs}`,
    options: { headers }
  });
});

export const getAllBookingsAdmin = (filters = {}) => handleApiCall(async function getAllBookingsAdmin() {
  const queryParams = new URLSearchParams();

  // Add filters if provided
  if (filters.status) queryParams.append('status', filters.status);
  if (filters.courseId) queryParams.append('courseId', filters.courseId);
  if (filters.paymentStatus) queryParams.append('paymentStatus', filters.paymentStatus);
  if (filters.startDate) queryParams.append('startDate', filters.startDate);
  if (filters.endDate) queryParams.append('endDate', filters.endDate);
  // Outstanding balance filter — restricts results to balanceDue > 0 server-side
  if (filters.balanceDueOnly) queryParams.append('balanceDueOnly', 'true');

  // Add search parameter - CRITICAL for searching
  if (filters.search) queryParams.append('search', filters.search);

  // Add pagination parameters - increase limit for course-wide searches
  const searchLimit = filters.courseId && filters.courseId !== 'all' ? '1000' : (filters.limit || '50');
  queryParams.append('limit', searchLimit);
  // Backend expects 'lastKey' not 'nextToken'
  if (filters.nextToken) queryParams.append('lastKey', filters.nextToken);
  
  const queryString = queryParams.toString();
  const path = queryString ? `admin/bookings?${queryString}` : 'admin/bookings';
  
  return await get({
    apiName: API_NAME,
    path: path,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get detailed booking information for admin
 * ADMIN ONLY - requires admin group
 */
export const getBookingDetailsAdmin = (bookingId) => handleApiCall(async function getBookingDetailsAdmin() {
  return await get({
    apiName: API_NAME,
    path: `admin/bookings/${bookingId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Update booking as admin (can update any field)
 * ADMIN ONLY - requires admin group
 */
export const updateBookingAdmin = (bookingId, updateData) => handleApiCall(async function updateBookingAdmin() {
  return await put({
    apiName: API_NAME,
    path: `admin/bookings/${bookingId}`,
    options: {
      body: updateData,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Update admin-only notes on a booking. Stored as a top-level `adminNotes`
 * field on BookingTable; never visible to students or parents.
 * ADMIN ONLY.
 */
export const updateBookingAdminNotes = (bookingId, notes) => handleApiCall(async function updateBookingAdminNotes() {
  return await put({
    apiName: API_NAME,
    path: `admin/bookings/${bookingId}/admin-notes`,
    options: {
      body: { notes },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Update booking status as admin
 * ADMIN ONLY - requires admin group
 */
export const updateBookingStatusAdmin = (bookingId, status, reason = '') => handleApiCall(async function updateBookingStatusAdmin() {
  return await put({
    apiName: API_NAME,
    path: `admin/bookings/${bookingId}/status`,
    options: {
      body: { status, reason },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Cancel booking as admin
 * ADMIN ONLY - requires admin group
 * @param {string} bookingId - The booking ID to cancel
 * @param {string} reason - Cancellation reason
 * @param {boolean} releaseSlot - Whether to release the course slot (default: false)
 * @param {Object} options - Additional options
 * @param {boolean} options.zeroBalance - Zero out the balance due (default: false)
 * @param {number} options.cancellationFee - Cancellation fee to apply (default: 0)
 * @param {boolean} options.createRefund - Create a refund payment record (default: false)
 */
export const cancelBookingAdmin = (bookingId, reason = 'Cancelled by admin', releaseSlot = false, options = {}) => handleApiCall(async function cancelBookingAdmin() {
  const { zeroBalance = false, cancellationFee = 0, createRefund = false } = options;
  return await post({
    apiName: API_NAME,
    path: `admin/bookings/${bookingId}/cancel`,
    options: {
      body: {
        reason,
        releaseSlot,
        zeroBalance,
        cancellationFee,
        createRefund
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Force create booking for any user in any course (Admin Override)
 * Bypasses ALL validation including capacity, gender slots, eligibility
 * ADMIN ONLY - requires admin group
 *
 * @param {Object} bookingData
 * @param {string} bookingData.studentEmail - Email of the student to book
 * @param {string} bookingData.courseId - ID of the course to book into
 * @param {string} [bookingData.status='confirmed'] - Booking status
 * @param {string} [bookingData.paymentStatus='pending'] - Payment status
 * @param {string} [bookingData.notes=''] - Admin notes/reason for override
 * @param {boolean} [bookingData.bypassSlotAllocation=true] - If true, doesn't count against capacity
 */
export const forceCreateBookingAdmin = (bookingData) => handleApiCall(async function forceCreateBookingAdmin() {
  return await post({
    apiName: API_NAME,
    path: 'admin/bookings/force-create',
    options: {
      body: bookingData,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get all bookings for a specific course
 * ADMIN ONLY - requires admin group
 */
export const getCourseBookingsAdmin = (courseId, params = {}) => handleApiCall(async function getCourseBookingsAdmin() {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.lastKey) queryParams.append('lastKey', params.lastKey);
  
  // Add flag to get ALL results (for downloads)
  if (params.getAllResults) queryParams.append('getAllResults', 'true');
  
  const queryString = queryParams.toString();
  return await get({
    apiName: API_NAME,
    path: `admin/courses/${courseId}/bookings${queryString ? '?' + queryString : ''}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Export bookings data
 * ADMIN ONLY - requires admin group
 */
export const exportBookingsAdmin = async (options = {}) => {
  const { format = 'csv', ...filters } = options;
  
  try {
    console.log('Exporting bookings with options:', options);
    
    // Get auth headers
    const headers = await getAuthHeaders();
    
    // Build the request
    const apiRequest = {
      apiName: API_NAME,
      path: 'admin/bookings/export',
      options: {
        body: {
          format,
          ...filters
        },
        headers: headers
      }
    };
    
    console.log('Making export API request:', apiRequest);
    
    // Make the API call
    const response = await post(apiRequest);
    
    console.log('Raw export response:', response);
    
    // Handle the Amplify response wrapper
    let csvContent = '';
    
    // Check if response has the Promise wrapper structure
    if (response && response.response && typeof response.response.then === 'function') {
      console.log('Response has Promise wrapper, awaiting...');
      
      // Await the promise to get the actual response
      const actualResponse = await response.response;
      console.log('Actual response after await:', actualResponse);
      console.log('Actual response type:', typeof actualResponse);
      console.log('Response constructor:', actualResponse?.constructor?.name);
      
      // Check what properties the response has
      if (actualResponse) {
        console.log('Response properties:', Object.keys(actualResponse));
        console.log('Has body?', 'body' in actualResponse);
        console.log('Has text method?', typeof actualResponse.text === 'function');
        console.log('Has json method?', typeof actualResponse.json === 'function');
      }
      
      // Now extract the CSV content from the actual response
      if (typeof actualResponse === 'string') {
        csvContent = actualResponse;
      } else if (actualResponse && actualResponse.body) {
        // Check if body is a ReadableStream
        if (actualResponse.body instanceof ReadableStream || 
            (actualResponse.body && typeof actualResponse.body.text === 'function')) {
          console.log('Body is a ReadableStream, reading text...');
          csvContent = await actualResponse.body.text();
        } else if (typeof actualResponse.body === 'string') {
          csvContent = actualResponse.body;
        } else {
          console.log('Unknown body type:', actualResponse.body);
        }
      } else if (actualResponse && actualResponse.text && typeof actualResponse.text === 'function') {
        csvContent = await actualResponse.text();
      } else {
        console.error('Could not extract CSV content from response');
      }
    } else if (typeof response === 'string') {
      // Direct string response
      csvContent = response;
    } else if (response && response.body) {
      // Response with body property
      csvContent = response.body;
    } else {
      console.error('Unexpected response format:', response);
    }
    
    console.log('CSV content type:', typeof csvContent);
    console.log('CSV content length:', csvContent ? csvContent.length : 'undefined');
    
    // Only try substring if csvContent is actually a string
    if (csvContent && typeof csvContent === 'string') {
      console.log('First 100 chars:', csvContent.substring(0, 100));
    } else {
      console.log('CSV content is not a string, it is:', csvContent);
    }
    
    if (format === 'csv' && csvContent && typeof csvContent === 'string') {
      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.setAttribute('href', url);
      link.setAttribute('download', `bookings-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Export completed' };
    }
    
    return { success: false, message: 'No data to export or invalid format' };
    
  } catch (error) {
    console.error('Error in exportBookingsAdmin:', error);
    throw error;
  }
};

/**
 * Send acknowledgement email for a booking
 * ADMIN ONLY - requires admin group
 * @param {string} bookingId - The booking ID
 * @param {Object|string} options - Email options (templateId, customSubject) or legacy emailType string
 */
export const sendAcknowledgementEmail = (bookingId, options = {}) => handleApiCall(async function sendAcknowledgementEmail() {
  // Support both new object format and legacy string format
  const body = typeof options === 'string'
    ? { emailType: options }
    : {
        templateId: options.templateId,
        emailType: options.emailType || 'custom',
        customSubject: options.customSubject
      };

  return await post({
    apiName: API_NAME,
    path: `admin/bookings/${bookingId}/send-acknowledgement`,
    options: {
      body,
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Send bulk emails to multiple bookings
 * ADMIN ONLY - requires admin group
 */
export const sendBulkEmails = (bookingIds, emailOptions) => handleApiCall(async function sendBulkEmails() {
  return await post({
    apiName: API_NAME,
    path: 'admin/bookings/bulk-email',
    options: {
      body: {
        bookingIds,
        ...emailOptions
      },
      headers: await getAuthHeaders()
    }
  });
});

// ============================================
// WAITLIST FUNCTIONS
// ============================================

/**
 * Join waitlist for a course
 */
export const joinWaitlist = (courseId, studentData) => handleApiCall(async function joinWaitlist() {
  return await post({
    apiName: API_NAME,
    path: 'waitlist/join',
    options: {
      body: {
        courseId,
        ...studentData
      },
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Get current student's waitlist entries
 */
export const getMyWaitlist = () => handleApiCall(async function getMyWaitlist() {
  return await get({
    apiName: API_NAME,
    path: 'students/me/waitlist',
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Admin: Get all waitlists
 * ADMIN ONLY - requires admin group
 */
export const getAllWaitlistsAdmin = () => handleApiCall(async function getAllWaitlistsAdmin() {
  return await get({
    apiName: API_NAME,
    path: 'admin/waitlist',
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Admin: Get waitlist for specific course
 * ADMIN ONLY - requires admin group
 */
export const getCourseWaitlistAdmin = (courseId) => handleApiCall(async function getCourseWaitlistAdmin() {
  return await get({
    apiName: API_NAME,
    path: `admin/waitlist/${courseId}`,
    options: {
      headers: await getAuthHeaders()
    }
  });
});

/**
 * Admin: Notify waitlisted students
 * ADMIN ONLY - requires admin group
 */
export const notifyWaitlist = (courseId, options = {}) => handleApiCall(async function notifyWaitlist() {
  return await post({
    apiName: API_NAME,
    path: `admin/waitlist/${courseId}/notify`,
    options: {
      body: options,
      headers: await getAuthHeaders()
    }
  });
});

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Check if a booking is expired
 */
export const isBookingExpired = (booking) => {
  if (!booking.expiresAt || booking.status !== 'slot_reserved') {
    return false;
  }
  return new Date(booking.expiresAt) < new Date();
};

/**
 * Calculate time remaining for booking slot
 */
export const getTimeRemaining = (expiresAt) => {
  if (!expiresAt) return null;
  
  const now = new Date();
  const expires = new Date(expiresAt);
  const diff = expires - now;
  
  if (diff <= 0) return { expired: true };
  
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  
  return {
    expired: false,
    minutes,
    seconds,
    formatted: `${minutes}:${seconds.toString().padStart(2, '0')}`
  };
};

/**
 * Format booking status for display
 */
export const formatBookingStatus = (status) => {
  const statusMap = {
    'slot_reserved': 'Slot Reserved',
    'form_in_progress': 'Form In Progress',
    'pending_payment': 'Pending Payment',
    'payment_processing': 'Payment Processing',
    'confirmed': 'Confirmed',
    'cancelled': 'Cancelled',
    'expired': 'Expired',
    'waitlisted': 'Waitlisted'
  };
  
  return statusMap[status] || status;
};

/**
 * Format payment status for display
 */
export const formatPaymentStatus = (status) => {
  const statusMap = {
    'pending': 'Payment Pending',
    'processing': 'Processing',
    'completed': 'Paid',
    'partial': 'Partially Paid',
    'failed': 'Payment Failed',
    'refunded': 'Refunded',
    'overdue': 'Overdue'
  };
  
  return statusMap[status] || status;
};

// ============================================
// RESILIENT BOOKING FUNCTIONS
// ============================================

/**
 * Monitor booking system health
 */
export const getBookingSystemHealth = () => {
  return {
    circuitBreakerState: resilientClient.getCircuitState(),
    timestamp: new Date().toISOString()
  };
};

/**
 * Reset circuit breaker (for admin use)
 */
export const resetBookingCircuit = () => {
  resilientClient.resetCircuit();
  console.log('🔄 Booking circuit breaker reset');
};

/**
 * Create batch bookings with retry logic
 */
export const createBatchBookings = async (bookingsData, options = {}) => {
  const { onProgress } = options;
  
  console.log(`📚 Creating ${bookingsData.length} bookings in batch...`);
  
  const results = [];
  let successCount = 0;
  let failureCount = 0;
  
  // Process bookings with controlled concurrency
  for (let i = 0; i < bookingsData.length; i++) {
    try {
      const booking = await createBookingWithRetry(bookingsData[i], {
        maxAttempts: 3,
        onRetry: (attempt, delay) => {
          console.log(`Retry attempt ${attempt} for booking ${i + 1}, waiting ${delay}ms`);
        }
      });
      
      results.push({
        index: i,
        success: true,
        booking
      });
      successCount++;
      
    } catch (error) {
      results.push({
        index: i,
        success: false,
        error: error.message
      });
      failureCount++;
    }
    
    // Report progress
    if (onProgress) {
      onProgress({
        total: bookingsData.length,
        completed: i + 1,
        successCount,
        failureCount
      });
    }
    
    // Small delay between bookings to prevent overwhelming the server
    if (i < bookingsData.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }
  
  console.log(`✅ Batch booking complete: ${successCount} success, ${failureCount} failed`);
  
  return {
    results,
    summary: {
      total: bookingsData.length,
      success: successCount,
      failed: failureCount
    }
  };
};